import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Award, 
  Users, 
  Building, 
  Heart,
  MapPin
} from 'lucide-react';

const TERRY_IMAGE = "https://customer-assets.emergentagent.com/job_527e7f90-3125-4a88-b7e0-91a8350441c4/artifacts/hg0e6hd8_TK%20Headshot%20-%202026.png";

export const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-amber-700/10 rounded-3xl transform -rotate-3"></div>
                <img
                  src={TERRY_IMAGE}
                  alt="Terry Katz"
                  className="relative rounded-2xl shadow-2xl w-full object-cover"
                  data-testid="about-image"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white rounded-xl shadow-lg p-6 hidden md:block">
                <div className="flex items-center gap-4">
                  <MapPin className="w-8 h-8 text-amber-500" />
                  <div>
                    <p className="font-semibold text-lg">Philadelphia Native</p>
                    <p className="text-sm text-slate-300">Serving PA, NJ, FL & NY</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">About Me</p>
              <h1 
                className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-slate-900 mb-6"
                data-testid="about-title"
              >
                Meet Terry Katz
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-6">
                Vice President of Sales & Marketing at <a href="https://www.alegaltitle.com" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">American Legal Abstract</a>, with over 15 years 
                helping real estate professionals and homeowners navigate the title process.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                I'm a third-generation builder's son who grew up understanding real estate from 
                the ground up. That background taught me the importance of getting details right 
                and building relationships that last.
              </p>
              <Link to="/contact" data-testid="about-cta">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-4 font-medium transition-all hover:scale-105">
                  <Phone className="w-5 h-5 mr-2" />
                  Let's Connect
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Background Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="background-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              My Story
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Growing up in a family of builders, I learned early that success in real estate 
              comes down to trust, communication, and follow-through.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8" data-testid="story-card-1">
              <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center mb-6">
                <Building className="w-6 h-6 text-amber-700" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-slate-900 mb-4">
                Third-Generation Builder Background
              </h3>
              <p className="text-slate-600 leading-relaxed">
                My grandfather and father were builders in the Philadelphia area, and I grew up on job sites from an early age. I started as a laborer and worked my way into sales, playing an active role in the development of dozens of communities and thousands of homes throughout Bucks and Montgomery Counties, PA.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                That experience gave me a practical understanding of how deals come together, and a respect for the fact that behind every transaction are real people with real concerns. It is a perspective I bring to every deal today.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8" data-testid="story-card-2">
              <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center mb-6">
                <Award className="w-6 h-6 text-amber-700" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-slate-900 mb-4">
                15+ Years in Title
              </h3>
              <p className="text-slate-600 leading-relaxed">
                I've spent over a decade and a half in the title industry, handling everything from straightforward purchases to complex commercial deals and probate situations. My family was also part of a title company for 15 years, which gave me early exposure to the business and a deeper understanding of how deals get to the closing table.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                There is very little I have not seen or solved, and I bring that experience to every transaction.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8" data-testid="story-card-3">
              <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-amber-700" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-slate-900 mb-4">
                Relationships First
              </h3>
              <p className="text-slate-600 leading-relaxed">
                I pick up the phone, explain things in plain English, and stay involved when things get complicated. Clients come back because they know I am in their corner.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8" data-testid="story-card-4">
              <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-6 h-6 text-amber-700" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-slate-900 mb-4">
                Philly Born and Raised
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Philadelphia is home. I know the neighborhoods, understand the local market, and 
                have deep connections throughout the PA and NJ real estate community. When you 
                work with me, you get local expertise that matters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* American Legal Abstract Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="company-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-amber-500 font-medium tracking-wide uppercase text-sm mb-4">My Company</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold mb-6">
              <a href="https://www.alegaltitle.com" target="_blank" rel="noopener noreferrer" className="hover:underline">American Legal Abstract</a>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              As Vice President of Sales & Marketing at <a href="https://www.alegaltitle.com" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">American Legal Abstract</a>, I'm backed by a 
              full-service title insurance and settlement company with the resources, expertise, 
              and commitment to handle any transaction – simple or complex.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed">
              Our team is dedicated to providing accurate title searches, reliable insurance, 
              and professional settlements. We serve clients throughout Pennsylvania, New Jersey, 
              serving the greater Philadelphia region with integrity and excellence.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
